import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import { UserSettingsConfiguration } from '../models/userSettingConfiguration';
import { login, LoginData, logout } from '../services/api/authService';
import { removeToken, setToken } from '../services/storageService';
import { logLastLogin } from '../services/api/employeeServices';

export const loginThunk = createAsyncThunk<
  { token: string; userDetails: UserSettingsConfiguration },
  LoginData,
  { rejectValue: string }
>('auth/login', async (loginData, thunkApi) => {
  try {
    const loginResponse = await login(loginData);
    setToken(loginResponse.token);

    return {
      token: loginResponse.token,
      userDetails: loginResponse.userDetails,
    };
  } catch (err) {
    console.error(err);

    return thunkApi.rejectWithValue('Invalid credentials');
  }
});

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    const { token } = getState() as AuthState;

    if (token) {
      await logout(token);
    }

    removeToken();

    return true;
  }
);

export interface AuthState {
  token: string | null;
  isLoading: boolean;
  error: null | unknown;
  userDetails: UserSettingsConfiguration | null;
  authTime: number | null;
}

const initialState: AuthState = {
  token: null,
  isLoading: false,
  error: null,
  userDetails: null,
  authTime: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserToken: (state, action) => {
      state.token = action.payload;
    },
    updateAuthTime: (state, action) => {
      state.authTime = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.token = null;
      })
      .addCase(
        loginThunk.fulfilled,
        (state, { payload: { token, userDetails } }) => {
          state.isLoading = false;
          state.error = null;
          state.token = token;
          state.userDetails = userDetails;
        }
      )
      .addCase(loginThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        state.token = null;
      });
  },
});

export const authReducer = authSlice.reducer;

export const { setUserToken, updateAuthTime } = authSlice.actions;

// selectors
export const selectToken = (root: RootState): string | null => root.auth.token;
export const selectUserId = (root: RootState): string | null =>
  root.auth.userDetails?.nameid || null;
export const selectIsLoggedIn = (root: RootState): boolean => !!root.auth.token;

export const logLastLoginThunk = createAsyncThunk<void, number>(
  'auth/logLastLogin',
  async (userAuthTime, { dispatch, getState }) => {
    const authTime = (getState() as RootState).auth.authTime;

    if (authTime !== userAuthTime) {
      logLastLogin();
      dispatch(updateAuthTime(userAuthTime));
    }
  }
);
